import * as React from 'react';
import { Link } from 'react-router-dom';

import { PaddedSection, UploadAndCheckTableFiltersBar } from 'common/components';
import { FilterData } from 'common/helpers/url';
import { generateColumns } from 'common/helpers/utils';
import { PaginatedTable } from 'features/pagination/components/PaginatedTable';
import { StatusPill } from 'common/components/StatusPill';
import { AutocompleteField, PaginationView } from 'features/pagination/models/paginationModel';
import { Antd3Form, Antd3FormProps } from 'common/components/deprecated/antd3';
import { EditPaths } from 'features/edit/models/editModel';
import { EditContractPaths } from 'features/contract/models/contractModels';

import { CheckBenefit, CheckPaths } from '../models/checkModel';
import { getCheckEvidencesBenefitsSuccess } from '../actions/checkActions';
import { ApprovedProps as ApprovedContainerProps } from '../containers/ApprovedContainer';
import { getStatus } from 'features/organisation/selectors/organisationSelectors';

const checkEvidencesTableColumns = [
    {
        title: 'Benefit',
        key: 'outcome',
        render: (outcome: string, { id, referenceNumber, contract }: CheckBenefit) => (
            <Link
                to={`${CheckPaths.Evidence}${EditPaths.Contract}/${contract}/benefit/${id}`}
            >{`${outcome} | ${referenceNumber}`}</Link>
        ),
    },
    {
        title: 'Benefit Label',
        key: 'label',
        render: (label: string, { contract, id }: CheckBenefit) => (
            <Link to={`${CheckPaths.Evidence}${EditPaths.Contract}/${contract}/benefit/${id}`}>
                {label}
            </Link>
        ),
    },
    {
        title: 'Quantity',
        key: 'quantity',
        render: (quantity: string) => quantity,
    },
    {
        title: 'Status',
        key: 'status',
        render: (status: string) => <StatusPill status={status} />,
    },
    {
        title: 'Completion Date',
        key: 'finalDeliveryDate',
        render: (finalDeliveryDate: string) => finalDeliveryDate,
    },
    {
        title: 'Project',
        key: 'contractTitle',
        render: (contractTitle: string, { contract, contractReferenceNumber }: CheckBenefit) => (
            <Link to={`${EditPaths.Contract}/${contract}${EditContractPaths.ContractDetails}`}>
                {`${contractTitle} | ${contractReferenceNumber}`}
            </Link>
        ),
    },
];

type ApprovedEvidenceProps = Antd3FormProps & ApprovedContainerProps;

class ApprovedEvidencesForm extends React.Component<ApprovedEvidenceProps> {
    private refreshTable = (filters?: FilterData) =>
        this.props.paginationRequest({
            view: PaginationView.ApprovedCheckEvidence,
            pagination: {
                current: 1,
            },
            filters,
            paginationSuccessCallback: getCheckEvidencesBenefitsSuccess,
        });

    private saveBenefitFilters = (benefitReferenceNumber: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedCheckEvidence,
            values: {
                referenceNumber: benefitReferenceNumber,
            },
        });

    private saveContractFilters = (contractId: number | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedCheckEvidence,
            values: {
                contract: contractId,
            },
        });

    private saveExecutiveOrganisationFilter = (executive: string | undefined) =>
        this.props.saveFilters({
            paginatedView: PaginationView.ApprovedCheckEvidence,
            values: { executive },
        });

    private searchByBenefits = (benefitReferenceNumber: string) => {
        this.saveBenefitFilters(benefitReferenceNumber);
        this.refreshTable();
    };

    private searchByContracts = (contractId: number) => {
        this.saveContractFilters(contractId);
        this.refreshTable();
    };

    private searchByExecutiveOrganisation = (executive: string) => {
        this.saveExecutiveOrganisationFilter(executive);
        this.refreshTable();
    };

    private onRemoveBenefitAutocomplete = () =>
        this.saveBenefitFilters(undefined) && this.refreshTable();

    private onRemoveContractAutocomplete = () =>
        this.saveContractFilters(undefined) && this.refreshTable();

    private onRemoveExecutiveOrganisationAutocomplete = () => {
        this.saveExecutiveOrganisationFilter(undefined);
        this.refreshTable();
    };

    private clearFilters = () =>
        this.props.clearFilters(PaginationView.ApprovedCheckEvidence) &&
        this.refreshTable() &&
        this.props.form.setFieldsValue({
            quickFilterBenefitReferenceNumber: '',
            quickFilterContractReferenceNumber: '',
        });

    public render(): JSX.Element {
        const { form, filter, approvedEvidences, isTenant } = this.props;

        return (
            <PaddedSection header="Check Approved Evidence">
                <UploadAndCheckTableFiltersBar
                    form={form}
                    searchByBenefits={this.searchByBenefits}
                    onRemoveBenefitAutocomplete={this.onRemoveBenefitAutocomplete}
                    searchByContracts={this.searchByContracts}
                    onRemoveContractAutocomplete={this.onRemoveContractAutocomplete}
                    searchByExecutiveOrganisation={this.searchByExecutiveOrganisation}
                    onRemoveExecutiveOrganisationAutocomplete={
                        this.onRemoveExecutiveOrganisationAutocomplete
                    }
                    filter={filter}
                    clearFilters={this.clearFilters}
                    benefitFilterAutocompleteField={AutocompleteField.ApprovedCheckEvidence}
                    isTenant={isTenant}
                />
                <PaginatedTable
                    view={PaginationView.ApprovedCheckEvidence}
                    paginationSuccessCallback={getCheckEvidencesBenefitsSuccess}
                    data={approvedEvidences}
                    columns={generateColumns(checkEvidencesTableColumns)}
                    emptyText="No evidence has been approved"
                />
            </PaddedSection>
        );
    }
}

export const ApprovedEvidences = Antd3Form.create({})(ApprovedEvidencesForm);
